import { apiService } from '@/shared/services/apiServices';
import { AxiosError } from 'axios';
import { SUBSCRIPTION_ERROR_MESSAGES } from '../errors';

// ============================================================================
// API request payload (backend contract)
// ============================================================================

export type DurationType = 'MONTHLY' | 'YEARLY';
export type BillingCycle = 'MONTHLY' | 'QUARTERLY' | 'YEARLY';

export interface CreateSubscriptionPlanServicePayload {
  serviceTypeId: string;
  // Only means something alongside a discount — omitted when there is none.
  usageLimit?: number;
  basePrice: number;
  discountPercentage?: number;
}

export interface CreateSubscriptionPlanEquipmentCategoryPayload {
  equipmentCategoryId: string;
  basePrice: number;
}

export interface CreateSubscriptionPlanDiscountTierPayload {
  equipmentRank: number;
  discountPercentage: number;
}

export interface CreateSubscriptionPlanPayload {
  planName: string;
  planDescription: string;
  durationType: DurationType;
  duration: number;
  billingCycle: BillingCycle;
  price: number;
  limits?: {
    numberOfUsers?: number;
    numberOfFacilities?: number;
    numberOfQmsDocuments?: number;
  };
  offerId?: string;
  services: CreateSubscriptionPlanServicePayload[];
  equipmentCategories: CreateSubscriptionPlanEquipmentCategoryPayload[];
  discountTiers: CreateSubscriptionPlanDiscountTierPayload[];
}

// ============================================================================
// Input from form (UI values)
// ============================================================================

export interface CreateSubscriptionPlanServiceInput {
  serviceTypeId: string;
  usageLimit: string; // numeric string from the form
  basePrice: string; // may contain spaces as thousand separator, like price
  discountPercentage?: string; // numeric string, 0-100
}

export interface CreateSubscriptionPlanEquipmentCategoryInput {
  equipmentCategoryId: string;
  basePrice: string;
}

export interface CreateSubscriptionPlanDiscountTierInput {
  equipmentRank: number; // 1-7
  discountPercentage: string; // numeric string, 0-100
}

export interface CreateSubscriptionPlanInput {
  planName: string;
  planDescription: string;
  durationType: string; // "Miesięczny" | "Roczny"
  period: string; // e.g. "1 miesiąc", "12 miesiące", "1 rok", "5 lat"
  cycle: string; // "Miesięczny" | "kwartalny" | "Roczny"
  price: string; // may contain spaces as thousand separator
  userCount?: string;
  facilityCount?: string;
  qmsDocCount?: string;
  offerId?: string;
  services: CreateSubscriptionPlanServiceInput[];
  equipmentCategories: CreateSubscriptionPlanEquipmentCategoryInput[];
  discountTiers: CreateSubscriptionPlanDiscountTierInput[];
}

// ============================================================================
// Result
// ============================================================================

export interface CreateSubscriptionPlanResult {
  success: boolean;
  data?: unknown;
  error?: string;
  /** Field-level errors from API validation (e.g. 400 with details[]). */
  fieldErrors?: Record<string, string>;
}

// ============================================================================
// Error messages (re-export for consumers)
// ============================================================================

export const ERROR_MESSAGES = {
  DEFAULT_ERROR: SUBSCRIPTION_ERROR_MESSAGES.DEFAULT_ERROR,
  NETWORK_ERROR: SUBSCRIPTION_ERROR_MESSAGES.NETWORK_ERROR,
  UNEXPECTED_ERROR: SUBSCRIPTION_ERROR_MESSAGES.UNEXPECTED_ERROR,
  PLAN_NAME_ALREADY_EXISTS:
    SUBSCRIPTION_ERROR_MESSAGES.PLAN_NAME_ALREADY_EXISTS,
  UNAUTHORIZED: SUBSCRIPTION_ERROR_MESSAGES.UNAUTHORIZED,
  VALIDATION_ERROR: SUBSCRIPTION_ERROR_MESSAGES.VALIDATION_ERROR,
  ZOD_VALIDATION: SUBSCRIPTION_ERROR_MESSAGES.ZOD_VALIDATION,
  INTERNAL_SERVER_ERROR: SUBSCRIPTION_ERROR_MESSAGES.INTERNAL_SERVER_ERROR,
} as const;

// ============================================================================
// Mappers: UI values → API enums / numbers
// ============================================================================

const mapDurationType = (durationType: string): DurationType => {
  return durationType === 'Roczny' ? 'YEARLY' : 'MONTHLY';
};

const PERIOD_TO_DURATION_MONTHLY: Record<string, number> = {
  '1 miesiąc': 1,
  '2 miesiące': 2,
  '3 miesiące': 3,
  '4 miesiące': 4,
  '5 miesiące': 5,
  '6 miesiące': 6,
  '7 miesiące': 7,
  '8 miesiące': 8,
  '9 miesiące': 9,
  '10 miesiące': 10,
  '11 miesiące': 11,
  '12 miesiące': 12,
};

const PERIOD_TO_DURATION_YEARLY: Record<string, number> = {
  '1 rok': 1,
  '2 lata': 2,
  '3 lata': 3,
  '4 lata': 4,
  '5 lat': 5,
  '6 lat': 6,
  '7 lat': 7,
  '8 lat': 8,
  '9 lat': 9,
  '10 lat': 10,
};

const getDuration = (durationType: string, period: string): number => {
  const map =
    durationType === 'Roczny'
      ? PERIOD_TO_DURATION_YEARLY
      : PERIOD_TO_DURATION_MONTHLY;
  const duration = map[period];
  if (duration === undefined) {
    return 1;
  }
  return duration;
};

const mapBillingCycle = (cycle: string): BillingCycle => {
  const normalized = (cycle || '').trim();
  if (normalized === 'kwartalny') {
    return 'QUARTERLY';
  }
  if (normalized === 'Roczny') {
    return 'YEARLY';
  }
  return 'MONTHLY';
};

/** Parse price string (may contain spaces) to number. */
const parsePrice = (price: string): number => {
  const cleaned = (price || '').replace(/\s/g, '').replace(',', '.');
  const value = parseFloat(cleaned);
  return Number.isNaN(value) ? 0 : value;
};

/** Parse an optional numeric string (may contain decimals) to a number, e.g. discount %. */
const parseFloatOptional = (s: string | undefined): number | undefined => {
  if (s === undefined || s.trim() === '') {
    return undefined;
  }
  const value = parseFloat(s.replace(',', '.'));
  return Number.isNaN(value) ? undefined : value;
};

/** Map a service form entry (string fields) to the API payload shape (numbers). */
export const mapService = (
  service: CreateSubscriptionPlanServiceInput
): CreateSubscriptionPlanServicePayload => {
  const usageLimit = parseIntOptional(service.usageLimit);
  const basePrice = parsePrice(service.basePrice);
  const discountPercentage = parseFloatOptional(service.discountPercentage);

  return {
    serviceTypeId: service.serviceTypeId,
    basePrice,
    ...(usageLimit !== undefined && { usageLimit }),
    ...(discountPercentage !== undefined && { discountPercentage }),
  };
};

/** Map an equipment category form entry (string fields) to the API payload shape (numbers). */
export const mapEquipmentCategory = (
  entry: CreateSubscriptionPlanEquipmentCategoryInput
): CreateSubscriptionPlanEquipmentCategoryPayload => ({
  equipmentCategoryId: entry.equipmentCategoryId,
  basePrice: parsePrice(entry.basePrice),
});

/** Map a discount-tier form entry (string fields) to the API payload shape (numbers). */
export const mapDiscountTier = (
  entry: CreateSubscriptionPlanDiscountTierInput
): CreateSubscriptionPlanDiscountTierPayload => ({
  equipmentRank: entry.equipmentRank,
  discountPercentage: parseFloatOptional(entry.discountPercentage) ?? 0,
});

const parseIntOptional = (s: string | undefined): number | undefined => {
  if (s === undefined || s === '') {
    return undefined;
  }
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? undefined : n;
};

// ============================================================================
// Error extraction (map API status + body to user message)
// ============================================================================

interface ApiErrorData {
  message?: string;
  error?: string;
  statusCode?: number;
  code?: string;
  details?: Array<{ path?: string; message?: string; code?: string }>;
}

const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 401) {
    return SUBSCRIPTION_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 409) {
    return SUBSCRIPTION_ERROR_MESSAGES.CONFLICT_PLAN_NAME;
  }
  if (status === 500) {
    return SUBSCRIPTION_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (status === 400) {
    const msg = getMessageFromPayload(errorData);
    if (msg && msg.toLowerCase().includes('invalid json in request body')) {
      return SUBSCRIPTION_ERROR_MESSAGES.VALIDATION_ERROR;
    }
    // Prefer first detail message when available (e.g. "Too big: expected number to be <=9007199254740991")
    const fieldErrors = getFieldErrorsFromPayload(errorData);
    const firstDetailMessage =
      fieldErrors && Object.keys(fieldErrors).length > 0
        ? Object.values(fieldErrors)[0]
        : null;
    return (
      firstDetailMessage || msg || SUBSCRIPTION_ERROR_MESSAGES.ZOD_VALIDATION
    );
  }

  const msg = getMessageFromPayload(errorData);
  return msg || SUBSCRIPTION_ERROR_MESSAGES.DEFAULT_ERROR;
};

function getFieldErrorsFromPayload(
  errorData: unknown
): Record<string, string> | undefined {
  if (!errorData || typeof errorData !== 'object') {
    return undefined;
  }
  const obj = errorData as ApiErrorData;
  const details = obj.details;
  if (!Array.isArray(details) || details.length === 0) {
    return undefined;
  }
  const fieldErrors: Record<string, string> = {};
  for (const d of details) {
    const path = d?.path;
    let message = d?.message;
    if (path && message) {
      const isTooBig =
        d?.code === 'too_big' || message.toLowerCase().includes('too big');
      if (isTooBig && path === 'price') {
        message = SUBSCRIPTION_ERROR_MESSAGES.PRICE_TOO_BIG;
      } else if (isTooBig && path.endsWith('basePrice')) {
        message = SUBSCRIPTION_ERROR_MESSAGES.INVALID_BASE_PRICE;
      }
      fieldErrors[path] = message;
    }
  }
  return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
}

function getMessageFromPayload(errorData: unknown): string | null {
  if (!errorData) {
    return null;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData !== 'object') {
    return null;
  }

  const obj = errorData as ApiErrorData;
  const message = obj.message || obj.error;
  if (message) {
    return message;
  }

  // Zod often returns { errors: [...] }
  const errors = (errorData as { errors?: unknown[] }).errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const first = errors[0];
    if (typeof first === 'string') {
      return first;
    }
    if (first && typeof first === 'object' && 'message' in first) {
      return String((first as { message: string }).message);
    }
  }

  return null;
}

// ============================================================================
// API call
// ============================================================================

const SUBSCRIPTIONS_ENDPOINT = 'loopay-subscription-management/subscriptions';

export const createSubscriptionPlan = async (
  input: CreateSubscriptionPlanInput
): Promise<CreateSubscriptionPlanResult> => {
  try {
    const durationType = mapDurationType(input.durationType);
    const duration = getDuration(input.durationType, input.period);
    const billingCycle = mapBillingCycle(input.cycle);
    const price = parsePrice(input.price);

    const limits: CreateSubscriptionPlanPayload['limits'] = {};
    const users = parseIntOptional(input.userCount);
    const facilities = parseIntOptional(input.facilityCount);
    const qmsDocs = parseIntOptional(input.qmsDocCount);
    if (users !== undefined && users >= 0) {
      limits.numberOfUsers = users;
    }
    if (facilities !== undefined && facilities >= 0) {
      limits.numberOfFacilities = facilities;
    }
    if (qmsDocs !== undefined && qmsDocs >= 0) {
      limits.numberOfQmsDocuments = qmsDocs;
    }

    const payload: CreateSubscriptionPlanPayload = {
      planName: input.planName.trim(),
      planDescription: (input.planDescription || '').trim(),
      durationType,
      duration,
      billingCycle,
      price,
      services: input.services.map(mapService),
      equipmentCategories: input.equipmentCategories.map(mapEquipmentCategory),
      discountTiers: input.discountTiers.map(mapDiscountTier),
    };

    if (Object.keys(limits).length > 0) {
      payload.limits = limits;
    }

    if (input.offerId) {
      payload.offerId = input.offerId;
    }

    const result = await apiService.postAPI(SUBSCRIPTIONS_ENDPOINT, payload, {
      skipGlobalErrorRedirect: true,
    });

    if (!result.error) {
      return {
        success: true,
        data: result.data,
      };
    }

    const errorMessage = extractErrorMessage(result.data, result.status);
    const fieldErrors = getFieldErrorsFromPayload(result.data);

    if (result.isNetworkError) {
      return {
        success: false,
        error: SUBSCRIPTION_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    return {
      success: false,
      error: errorMessage,
      ...(fieldErrors && { fieldErrors }),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      if (isNetworkError) {
        return {
          success: false,
          error: SUBSCRIPTION_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      const errorMessage = extractErrorMessage(errorData, status);
      const fieldErrors = getFieldErrorsFromPayload(errorData);
      return {
        success: false,
        error: errorMessage,
        ...(fieldErrors && { fieldErrors }),
      };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : SUBSCRIPTION_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
